import React, { useState, useEffect } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import CircularProgress from '@material-ui/core/CircularProgress';
import { axios, baseUrl } from 'Api';
import styles from './CloudPhotoAlbumModal.module.css';

const CloudPhotoAlbumModal = ({
  open,
  onClose,
  onAlbumSelected,
  photoCount = 0,
}) => {
  const [albumTitle, setAlbumTitle] = useState('');
  const [existingAlbums, setExistingAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});

  const getToken = () => localStorage.getItem('token');

  const fetchExistingAlbums = async () => {
    setLoadingAlbums(true);
    try {
      const response = await axios.post(`${baseUrl}/getAlbumList`, {
        token: getToken(),
      });

      if (response.data && response.data.data) {
        setExistingAlbums(response.data.data);
      } else {
        setExistingAlbums([]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching albums:', error);
      setExistingAlbums([]);
    } finally {
      setLoadingAlbums(false);
    }
  };

  // Fetch existing albums when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingAlbums();
      // Reset state
      setAlbumTitle('');
      setSelectedAlbum(null);
      setBrokenImages({});
    }
    // eslint-disable-next-line
  }, [open]);

  const handleCreateAlbum = async () => {
    if (!albumTitle.trim()) return;

    setIsCreating(true);
    try {
      const response = await axios.post(`${baseUrl}/addEditAlbum`, {
        title: albumTitle.trim(),
        description: '',
        visibility: '2', // All Viewers by default
        group_id: '',
        token: getToken(),
      });

      const albumId = response.data?.album_id;
      if (albumId) {
        onAlbumSelected({
          id: albumId,
          title: albumTitle.trim(),
          isNew: true,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating album:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectExistingAlbum = () => {
    if (!selectedAlbum) return;

    onAlbumSelected({
      id: selectedAlbum.id,
      title: selectedAlbum.title,
      isNew: false,
    });
  };

  const handleImageError = (albumId) => {
    setBrokenImages((prev) => ({ ...prev, [albumId]: true }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <PhotoLibraryIcon style={{ fontSize: 24, color: '#5aa5ef', marginRight: 12 }} />
            <h2 className={styles.title}>
              Upload
              {' '}
              {photoCount}
              {' '}
              Photo
              {photoCount !== 1 ? 's' : ''}
              {' '}
              to Album
            </h2>
          </div>
          <button className={styles.closeButton} onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </div>

        {/* Content - Two columns */}
        <div className={styles.content}>
          {/* Create Album Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <AddCircleOutlineIcon style={{ fontSize: 22, color: '#5aa5ef', marginRight: 10 }} />
              <span className={styles.sectionTitle}>Create New Album</span>
            </div>
            <div className={styles.sectionContent}>
              <p className={styles.sectionDescription}>
                Create a new album to organize your photos
              </p>
              <input
                type="text"
                value={albumTitle}
                onChange={(e) => setAlbumTitle(e.target.value)}
                placeholder="Enter album title..."
                className={styles.input}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateAlbum()}
              />
              <button
                type="button"
                onClick={handleCreateAlbum}
                disabled={!albumTitle.trim() || isCreating}
                className={styles.createButton}
              >
                {isCreating ? (
                  <>
                    <CircularProgress size={18} style={{ color: '#fff', marginRight: 8 }} />
                    Creating...
                  </>
                ) : (
                  'Create & Upload'
                )}
              </button>
            </div>
          </div>

          {/* Vertical Separator */}
          <div className={styles.separator}>
            <div className={styles.separatorLine} />
            <span className={styles.separatorText}>OR</span>
            <div className={styles.separatorLine} />
          </div>

          {/* Existing Albums Section */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <FolderOpenIcon style={{ fontSize: 22, color: '#5aa5ef', marginRight: 10 }} />
              <span className={styles.sectionTitle}>Choose Existing Album</span>
            </div>
            <div className={styles.sectionContent}>
              {loadingAlbums ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.spinner} />
                  <span className={styles.loadingText}>Loading albums...</span>
                </div>
              ) : existingAlbums.length === 0 ? (
                <div className={styles.emptyContainer}>
                  <FolderOpenIcon style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', marginBottom: 12 }} />
                  <span className={styles.emptyText}>No albums found</span>
                  <span className={styles.emptyHint}>Create a new album to get started</span>
                </div>
              ) : (
                <>
                  <div className={styles.albumList}>
                    {existingAlbums.map((album) => (
                      <div
                        key={album.id}
                        className={`${styles.albumItem} ${selectedAlbum?.id === album.id ? styles.albumItemSelected : ''}`}
                        onClick={() => setSelectedAlbum(album)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedAlbum(album)}
                      >
                        <div className={styles.albumIcon}>
                          {album.icon && !brokenImages[album.id] ? (
                            <img
                              src={album.icon}
                              alt={album.title}
                              className={styles.albumThumbnail}
                              onError={() => handleImageError(album.id)}
                            />
                          ) : (
                            <BrokenImageIcon style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)' }} />
                          )}
                        </div>
                        <div className={styles.albumInfo}>
                          <span className={styles.albumTitle}>{album.title}</span>
                          {album.photo_count && (
                            <span className={styles.albumCount}>
                              {album.photo_count}
                              {' '}
                              photos
                            </span>
                          )}
                        </div>
                        {selectedAlbum?.id === album.id && (
                          <CheckCircleIcon style={{ fontSize: 22, color: '#5aa5ef', flexShrink: 0 }} />
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectExistingAlbum}
                    disabled={!selectedAlbum}
                    className={styles.selectButton}
                  >
                    Add to Album
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudPhotoAlbumModal;
